import React, { useEffect, useState } from 'react';
import { BudgetDonut } from './BudgetDonut.tsx';
import { BudgetDetails } from './BudgetDetails.tsx';
import { LocationSearch } from './LocationSearch.tsx';
import { BudgetPercentageEditor } from './BudgetPercentageEditor.tsx'
import { useBudgetContext } from '../../../context/BudgetContext.tsx';
import { getBudget, updateBudgetSections, updateTotalBudget } from '../../../utils/budget.ts';
import { Settings, Loader2, MapPin } from 'lucide-react';
import { BudgetCategory } from './types.ts';
import { useAgentIntegration } from '../../../hooks/useAgentIntegration.ts';

export const BudgetView: React.FC = () => {
  const { budgetData, selectedCategory, updateBudget, selectCategory, updateLocation } = useBudgetContext();
  const [budget, setBudget] = useState<number>(0);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [categories, setCategories] = useState<BudgetCategory[]>([]); // Start with empty array
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0); // Prevent rapid refetches
  const [duplicatesFound, setDuplicatesFound] = useState<number>(0); // Track cleaned duplicates

  // Agent integration for real-time updates
  const { recentActions, refreshTrigger, markActionAsViewed } = useAgentIntegration('budget');

  const colorDictionary = budgetData.categories.reduce((acc, category) => {
    acc[category.name] = category.color; // Map name to color
    return acc;
  }, {} as Record<string, string>); // Initialize as an empty object with string keys and values

  const factorsDictionary = budgetData.categories.reduce((acc, category) => {
    acc[category.name] = category.factors; // Map name to factors
    return acc;
  }, {} as Record<string, any[]>); // Initialize as an empty object with string keys and array values

  const fetchBudgetData = async () => {
    // Prevent rapid refetches (minimum 2 seconds between fetches)
    const now = Date.now();
    if (now - lastFetchTime < 2000) {
      console.log('💰 Skipping fetch - too recent');
      return;
    }
    
    try {
      console.log('💰 Fetching budget data...');
      setIsLoading(true);
      setLastFetchTime(now);
      setDuplicatesFound(0); // Reset duplicate counter for new fetch
      
      const budgetSections = await getBudget();
      console.log('💰 Budget sections received:', budgetSections);
      
      // Clear existing categories first to prevent accumulation
      setCategories([]);
      
      const totalCost = budgetSections.reduce((acc, item) => acc + (item.cost || 0), 0);
      
      // Transform budgetSections to match BudgetCategory type with strict deduplication
      const seenIds = new Set<string>();
      const seenNames = new Set<string>();
      
      // First pass: group by category name and combine duplicates
      const categoryGroups = budgetSections.reduce((groups, item) => {
        const name = item.name;
        if (!name) return groups;
        
        if (!groups[name]) {
          groups[name] = [];
        }
        groups[name].push(item);
        return groups;
      }, {} as Record<string, any[]>);
      
      console.log('💰 Category groups:', Object.keys(categoryGroups).map(name => ({
        name,
        count: categoryGroups[name].length,
        entries: categoryGroups[name].map(item => ({ id: item.id, cost: item.cost, percentage: item.percentage }))
      })));
      
      // Second pass: create single entry per category by combining duplicates
      const transformedCategories = Object.entries(categoryGroups)
        .map(([categoryName, items]) => {
          // Use the most recent item (highest ID) as the base
          const baseItem = items.reduce((latest, current) => 
            current.id > latest.id ? current : latest
          );
          
          // Sum up costs and percentages from all duplicates
          const totalCost = items.reduce((sum, item) => sum + (item.cost || 0), 0);
          const totalPercentage = items.reduce((sum, item) => sum + (item.percentage || 0), 0);
          
          console.log(`💰 Combining ${items.length} "${categoryName}" entries:`, {
            totalCost,
            totalPercentage,
            baseId: baseItem.id
          });
          
          return {
            id: baseItem.id?.toString() || categoryName.toLowerCase().replace(/\s+/g, '-'),
            name: categoryName,
            amount: totalCost,
            percentage: totalPercentage,
            color: colorDictionary[categoryName] || '#FF6B6B',
            factors: factorsDictionary[categoryName] || []
          } as BudgetCategory;
        })
        .filter(category => 
          // Remove invalid categories
          category.name && 
          category.percentage > 0 && 
          category.color && 
          category.id
        );

      console.log('💰 Transformed categories count:', transformedCategories.length);
      console.log('💰 Transformed categories:', transformedCategories.map(c => ({
        name: c.name,
        cost: c.amount,
        percentage: c.percentage
      })));

      // Use API data if valid, otherwise fallback to default
      if (transformedCategories.length === 0) {
        console.log('💰 No valid API data, using default categories');
        setCategories([...budgetData.categories]);
        setBudget(budgetData.totalBudget);
        setDuplicatesFound(0); // No duplicates when using default data
      } else {
        // Calculate total cost from transformed categories (after deduplication)
        const calculatedTotal = transformedCategories.reduce((sum, cat) => sum + cat.amount, 0);
        
        // Ensure percentages add up to 100%
        const totalPercentage = transformedCategories.reduce((sum, cat) => sum + cat.percentage, 0);
        if (Math.abs(totalPercentage - 100) > 1) {
          console.log('💰 Normalizing percentages from', totalPercentage.toFixed(1), 'to 100%');
          const normalizedCategories = transformedCategories.map(cat => ({
            ...cat,
            percentage: (cat.percentage / totalPercentage) * 100
          }));
          setCategories(normalizedCategories);
        } else {
          setCategories(transformedCategories);
        }
        
        // Use calculated total from aggregated categories
        if (calculatedTotal > 0) {
          setBudget(calculatedTotal);
          console.log('💰 Total budget set to aggregated amount:', calculatedTotal);
        } else if (totalCost > 0) {
          setBudget(totalCost);
          console.log('💰 Total budget set to API total:', totalCost);
        }
        
        // Log summary of data cleanup
        const originalCount = budgetSections.length;
        const finalCount = transformedCategories.length;
        if (originalCount > finalCount) {
          console.log(`💰 ✅ Data cleanup completed: ${originalCount} entries → ${finalCount} unique categories`);
          setDuplicatesFound(originalCount - finalCount);
        }
      }

    } catch (error) {
      console.error('❌ Error fetching budget:', error);
      // Fallback to default data on error
      console.log('💰 Using default budget data due to error');
      setCategories([...budgetData.categories]); // Create new array
      setBudget(budgetData.totalBudget);
      setDuplicatesFound(0); // Reset on error
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load - fetch data once on mount
  useEffect(() => {
    console.log('💰 BudgetView: Initial mount, setting default data');
    setCategories([...budgetData.categories]);
    setBudget(budgetData.totalBudget);
    setDuplicatesFound(0); // Default data has no duplicates
    
    // Fetch real data after a short delay
    const timer = setTimeout(() => {
      fetchBudgetData();
    }, 500);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // Handle refresh triggers with debouncing
  useEffect(() => {
    if (refreshTrigger) {
      console.log('💰 BudgetView: refreshTrigger changed to:', refreshTrigger);
      // Debounce rapid refresh triggers
      const timer = setTimeout(() => {
        fetchBudgetData();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [refreshTrigger]);

  // Handle recent agent actions
  useEffect(() => {
    if (recentActions.length > 0) {
      console.log('💰 BudgetView: Recent actions:', recentActions);
      recentActions.forEach(action => {
        if (action.type === 'budget' && !action.executed) {
          console.log('💰 BudgetView: Processing budget action:', action);
          setTimeout(() => {
            markActionAsViewed(action.data.id);
          }, 3000);
        }
      });
    }
  }, [recentActions, markActionAsViewed]);

  const handleTotalBudgetChange = async (newTotal: number) => {
    setBudget(newTotal);
    await updateTotalBudget(newTotal)
  };

  const updateCategories = async (updatedCategories: BudgetCategory[]) => {
    // Ensure no duplicates in updated categories
    const uniqueUpdatedCategories = updatedCategories.filter((category, index, self) => 
      index === self.findIndex(c => c.id === category.id)
    );
    
    const totalAmount = uniqueUpdatedCategories.reduce((acc, category) => {
      return acc + (category.amount || 0);
    }, 0);
    
    setBudget(totalAmount);
    setCategories(uniqueUpdatedCategories);
    await updateBudgetSections(uniqueUpdatedCategories);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50/40 via-purple-50/30 to-orange-50/40 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Glassmorphism Header */}
        <div className="bg-white/30 backdrop-blur-xl rounded-3xl border border-white/20 shadow-[0_8px_32px_rgba(255,105,180,0.15)] p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-orange-500 bg-clip-text text-transparent">
                Budget Management
              </h1>
              <p className="text-gray-600 mt-2">Track and manage your wedding expenses with elegance</p>
            </div>
            <button
              onClick={() => setIsEditorOpen(true)}
              disabled={isLoading}
              className="flex items-center space-x-3 px-6 py-3 bg-white/40 backdrop-blur-xl border border-white/30 text-gray-700 rounded-2xl hover:bg-white/60 hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 group"
            >
              <div className="p-2 bg-gradient-to-r from-pink-500/20 to-orange-500/20 rounded-lg group-hover:scale-110 transition-transform">
                <Settings className="w-5 h-5 text-pink-600" />
              </div>
              <span className="font-medium">Edit Budget</span>
            </button>
          </div>

          {/* Location Section */}
          <div className="mt-6 pt-6 border-t border-white/20">
            <div className="flex items-center justify-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-pink-500/20 to-orange-500/20 rounded-xl">
                <MapPin className="w-6 h-6 text-pink-600" />
              </div>
              <LocationSearch 
                location={budgetData.location} 
                onLocationChange={updateLocation} 
              />
            </div>
          </div>
        </div>
         
        {/* Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Budget Visualization */}
          <div className="bg-white/25 backdrop-blur-xl rounded-3xl border border-white/20 shadow-[0_8px_32px_rgba(255,105,180,0.12)] p-8 hover:shadow-[0_12px_40px_rgba(255,105,180,0.2)] transition-all duration-500">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl">
                  <Settings className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="text-xl font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Budget Breakdown
                </h4>
              </div>
              {isLoading && (
                <div className="flex items-center space-x-3 text-sm text-gray-500 bg-white/40 backdrop-blur-sm rounded-full px-4 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Updating...</span>
                </div>
              )}
              {/* Show data cleanup indicator */}
              {duplicatesFound > 0 && !isLoading && (
                <div className="flex items-center space-x-2 text-xs text-orange-600 bg-orange-50/80 backdrop-blur-sm rounded-full px-3 py-1 border border-orange-200/60">
                  <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                  <span>{duplicatesFound} duplicates cleaned</span>
                </div>
              )}
            </div>
            <BudgetDonut
              categories={categories}
              budget={budget}
              selectedCategory={selectedCategory}
              onSelectCategory={selectCategory}
              onUpdateBudget={updateBudget}
              onTotalBudgetChange={handleTotalBudgetChange}
            />
          </div>

          {/* Budget Details */}
          <div className="bg-white/25 backdrop-blur-xl rounded-3xl border border-white/20 shadow-[0_8px_32px_rgba(255,105,180,0.12)] p-8 hover:shadow-[0_12px_40px_rgba(255,105,180,0.2)] transition-all duration-500">
            <div className="flex items-center space-x-4 mb-8">
              <div className="p-3 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="text-xl font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Category Details
              </h4>
            </div>
            <BudgetDetails
              category={selectedCategory}
              totalBudget={budget}
            />
          </div>
        </div>

        {/* Budget Editor Modal */}
        <BudgetPercentageEditor
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          categories={categories}
          totalBudget={budget}
          onSave={async (updatedCategories) => await updateCategories(updatedCategories)}
        />
      </div>
    </div>
  );
};